import { createContext, type ReactNode, useContext } from 'react'
import { CardIcon } from '@/components/CardIcon'
import { Dropdown, DropdownItem } from '@/components/Dropdown'
import { IconButton } from '@/components/IconButton'
import { Skeleton } from '@/components/Skeleton'
import { useSkeletonListContext } from '@/components/Skeleton/SkeletonList'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import styles from './BillingMethodList.module.css'
import {
  CardList,
  CardListItem,
  CardSubtitle,
  CardTitle,
} from '@/components/Card'
import { LabelChip } from '@/components/Chip'

export interface BillingMethodListProps {
  children?: ReactNode
  'aria-label'?: string
  'aria-labelledby'?: string
  mode?: 'select' | 'default'
}

/**
 * List of billing methods.
 */
export function BillingMethodList({
  children,
  mode = 'default',
  ...rest
}: BillingMethodListProps) {
  return (
    <SelectionModeContext.Provider value={mode}>
      <CardList {...rest}>{children}</CardList>
    </SelectionModeContext.Provider>
  )
}

export interface BillingMethodListItemProps {
  cardBrand: string
  cardLast4: string
  cardExpMonth: number
  cardExpYear: number
  isDefault?: boolean
  isDisabled?: boolean
  onSetAsDefault?: () => void
  onShowRemoveDialog?: () => void
  isPending?: boolean
  testId?: string
  setAsDefaultButtonTestId?: string
  removeButtonTestId?: string
}

/**
 * Billing method item.
 */
export function BillingMethodListItem({
  cardBrand,
  cardLast4,
  cardExpMonth,
  cardExpYear,
  isDefault,
  isDisabled,
  onSetAsDefault,
  onShowRemoveDialog,
  isPending,
  testId,
  setAsDefaultButtonTestId,
  removeButtonTestId,
}: BillingMethodListItemProps) {
  const isSpacious = useIsSpacious()
  const cardInfo = `${cardBrand} ending in ${cardLast4}`
  const expiration = `${cardExpMonth.toString().padStart(2, '0')}/${cardExpYear}`
  const textValue = `${cardInfo}, expires ${expiration}${isDefault ? ' (default payment method)' : ''}`
  const mode = useContext(SelectionModeContext)
  return (
    <CardListItem
      onAction={mode === 'select' && !isDefault ? onSetAsDefault : undefined}
      textValue={textValue}
      isDisabled={isDisabled || isPending}
      isActive={mode === 'select' ? isDefault : false}
      data-test={testId}
    >
      <div className={styles.itemLayout}>
        <div className={styles.iconContainer}>
          <CardIcon size={isSpacious ? 'lg' : 'base'} brand={cardBrand} />
        </div>

        <div className={styles.info}>
          <CardTitle>{cardInfo}</CardTitle>
          <CardSubtitle variant="description">
            Expires: <span className="text-strong">{expiration}</span>
          </CardSubtitle>
        </div>

        <div className={styles.extra}>
          {isDefault ? (
            <span className={styles.preferred}>
              <LabelChip text={mode === 'select' ? 'Selected' : 'Preferred'} />
            </span>
          ) : (
            <Dropdown
              title="More"
              trigger={
                <IconButton
                  isDisabled={isDisabled || isPending}
                  name="more"
                  label="Actions"
                />
              }
            >
              <DropdownItem
                icon="tick"
                onAction={onSetAsDefault}
                data-test={setAsDefaultButtonTestId}
              >
                Set as preferred payment method
              </DropdownItem>

              <DropdownItem
                icon="close"
                onAction={onShowRemoveDialog}
                data-test={removeButtonTestId}
              >
                Remove payment method
              </DropdownItem>
            </Dropdown>
          )}
        </div>
      </div>
    </CardListItem>
  )
}

/**
 * Skeleton component for billing method list items.
 */
export function BillingMethodListItemSkeleton() {
  const { varyPercent } = useSkeletonListContext()
  const isSpacious = useIsSpacious()
  return (
    <CardListItem textValue="Placeholder" isDisabled isSkeleton>
      <div className={styles.itemLayout}>
        <div className={styles.iconContainer}>
          <CardIcon
            size={isSpacious ? 'lg' : 'base'}
            isSkeleton
            brand="unknown"
          />
        </div>

        <div className={styles.info}>
          <CardTitle>
            <Skeleton width={varyPercent(40)} />
          </CardTitle>
          <CardSubtitle variant="description">
            <Skeleton width={100} />
          </CardSubtitle>
        </div>
        <div className={styles.extra}>
          <IconButton name="more" isSkeleton label="More" />
        </div>
      </div>
    </CardListItem>
  )
}

const SelectionModeContext =
  createContext<BillingMethodListProps['mode']>('default')
